import React, { useState } from 'react';
import { CheckCircle, Square, CheckSquare } from 'lucide-react';

const isDev = process.env.NODE_ENV === 'development';

/**
 * MultiSelectQuestion Component
 * 
 * Allows users to select multiple answers (A+C+D simultaneously).
 * Toggle behavior: clicking an option toggles its selection state.
 */
export default function MultiSelectQuestion({
  question,
  selectedAnswers = [],
  onSelectionChange,
  showResult = false,
  correctOptions = [],
  disabled = false
}) {
  const options = question?.options || [];
  
  // Handle toggling an option
  const handleToggle = (index) => {
    if (disabled || showResult) return;
    
    const newSelection = selectedAnswers.includes(index)
      ? selectedAnswers.filter(i => i !== index)
      : [...selectedAnswers, index];
    
    if (isDev) console.log(`[TEI_RENDERER] multi_select toggle: index=${index}, newSelection=${JSON.stringify(newSelection)}`);
    onSelectionChange(newSelection);
  };
  
  // Determine if an option is correct (for result display)
  const isOptionCorrect = (index) => {
    if (Array.isArray(correctOptions)) {
      return correctOptions.includes(index);
    }
    return false;
  };
  
  return (
    <div className="space-y-3" data-testid="multi-select-question">
      <p className="text-sm text-muted-foreground mb-4">
        Select all that apply
      </p>
      
      {options.map((option, index) => {
        const isSelected = selectedAnswers.includes(index);
        const isCorrect = isOptionCorrect(index);
        
        // Determine styling based on state
        let optionClass = "w-full p-4 rounded-xl border-2 text-left font-body flex items-center gap-3 transition-all";
        let icon = null;
        
        if (showResult) {
          if (isCorrect && isSelected) {
            // Correctly selected
            optionClass += " border-green-500 bg-green-50";
            icon = <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />;
          } else if (isCorrect && !isSelected) {
            // Should have been selected but wasn't
            optionClass += " border-amber-500 bg-amber-50";
            icon = <CheckSquare className="w-5 h-5 text-amber-500 flex-shrink-0" />;
          } else if (!isCorrect && isSelected) {
            // Incorrectly selected
            optionClass += " border-red-500 bg-red-50";
            icon = <Square className="w-5 h-5 text-red-500 flex-shrink-0" />;
          } else {
            // Correctly not selected
            optionClass += " border-border bg-background";
            icon = <Square className="w-5 h-5 text-muted-foreground flex-shrink-0" />;
          }
        } else {
          if (isSelected) {
            optionClass += " border-sage-500 bg-sage-50";
            icon = <CheckSquare className="w-5 h-5 text-sage-600 flex-shrink-0" />;
          } else {
            optionClass += " border-border bg-background hover:border-sage-300";
            icon = <Square className="w-5 h-5 text-muted-foreground flex-shrink-0" />;
          }
        }
        
        return (
          <button
            key={index}
            onClick={() => handleToggle(index)}
            disabled={disabled || showResult}
            className={optionClass}
            data-testid={`multi-select-option-${index}`}
          >
            {icon}
            <span className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-sm font-medium text-secondary-foreground flex-shrink-0">
              {String.fromCharCode(65 + index)}
            </span>
            <span className="flex-grow">{option}</span>
          </button>
        );
      })}
    </div>
  );
}
